import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, catchError, finalize, map, of, shareReplay, tap } from 'rxjs';
import { ILoginResponse } from '../../../types/ILoginResponse';
import { ENVIRONMENT } from '../../config/environment.token';

const TOKEN_KEY = 'organizae_token';
const EXPIRES_KEY = 'organizae_expires';
const ORG_KEY = 'organizae_org_id';
// Proactively refresh this many milliseconds before the access token expires.
const REFRESH_BUFFER_MS = 60_000;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly env = inject(ENVIRONMENT);

  private _refreshInFlight$: Observable<ILoginResponse> | null = null;
  private _refreshTimerId: ReturnType<typeof setTimeout> | null = null;

  private readonly _initialized$ = new BehaviorSubject<boolean>(false);
  /**
   * Emits false until startup session restoration is complete, then true.
   * Guards and interceptors can filter on this to wait for hydration before
   * acting on session state.
   */
  readonly initialized$ = this._initialized$.asObservable();

  /** Send credentials and persist session state on success. */
  login(email: string, password: string): Observable<ILoginResponse> {
    return this.http
      .post<ILoginResponse>(
        `${this.env.apiUrl}/api/Auth/login`,
        { email, password },
        { withCredentials: true },
      )
      .pipe(tap((res) => this.persistSession(res)));
  }

  /**
   * Exchange the HttpOnly refresh cookie for a fresh access token.
   * Concurrent callers share the same in-flight request so only one
   * POST /api/Auth/refresh is issued regardless of how many callers arrive.
   * On failure the local session is cleared and the error is re-thrown.
   */
  refresh(): Observable<ILoginResponse> {
    if (this._refreshInFlight$) {
      return this._refreshInFlight$;
    }

    this._refreshInFlight$ = this.http
      .post<ILoginResponse>(`${this.env.apiUrl}/api/Auth/refresh`, {}, { withCredentials: true })
      .pipe(
        tap((res) => this.persistSession(res)),
        catchError((err) => {
          this.clearSession();
          throw err;
        }),
        // finalize fires on the complete/error signal from the source (before shareReplay
        // forwards complete to subscribers), so the reference is cleared exactly once.
        finalize(() => {
          this._refreshInFlight$ = null;
        }),
        shareReplay(1),
      );

    return this._refreshInFlight$;
  }

  /**
   * Attempt to restore session state at app startup.
   * Short-circuits without a network call when a valid, non-expiring access
   * token is already in localStorage (token present and more than
   * REFRESH_BUFFER_MS from expiry).  Falls back to the refresh endpoint when
   * the token is absent, expired, or expiring soon.
   * Always resolves to true (valid session) or false (no session), never throws.
   * Sets initialized$ to true once the attempt is settled.
   */
  initializeSession(): Observable<boolean> {
    if (!this.isTokenExpiringSoon()) {
      // Token is valid and has enough time remaining — skip the network call.
      const expires = localStorage.getItem(EXPIRES_KEY);
      if (expires) this.scheduleRefresh(expires);
      this._initialized$.next(true);
      return of(true);
    }

    return this.refresh().pipe(
      map(() => true),
      catchError(() => of(false)),
      tap(() => this._initialized$.next(true)),
    );
  }

  /**
   * Returns true when the current access token is valid and not expiring soon.
   * When the token is absent, expired, or inside the refresh window, attempts
   * one refresh call and returns true on success or false on failure.
   * Never throws. Concurrent callers automatically share the single in-flight
   * refresh via refresh(), so no duplicate requests are issued.
   * Reusable by the auth guard (after hydration) and the HTTP interceptor
   * (before attaching the bearer token to outgoing requests).
   */
  ensureValidToken(): Observable<boolean> {
    if (!this.isTokenExpiringSoon()) {
      return of(true);
    }
    return this.refresh().pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  /**
   * Fire POST /api/Auth/logout best-effort (ignoring errors), immediately clear
   * local session state, and navigate to /login.
   */
  logout(): void {
    this.http
      .post<void>(`${this.env.apiUrl}/api/Auth/logout`, {}, { withCredentials: true })
      .pipe(catchError(() => of(void 0)))
      .subscribe();
    this.clearSession();
    this.router.navigate(['/login']);
  }

  changePassword(email: string, currentPassword: string, newPassword: string): Observable<void> {
    return this.http.post<void>(`${this.env.apiUrl}/api/Auth/change-password`, {
      email,
      currentPassword,
      newPassword,
    });
  }

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  getOrganizationId(): string {
    return localStorage.getItem(ORG_KEY) ?? '';
  }

  isAdmin(): boolean {
    return this.hasRole('Admin');
  }

  /**
   * Platform administrator. The admin panel is gated on this, not on `isAdmin()` —
   * `Admin` is a per-tenant role and carries no cross-organization authority.
   */
  isSuperAdmin(): boolean {
    return this.hasRole('SuperAdmin');
  }

  getCurrentUserEmail(): string {
    const email = this.getTokenPayload()?.['email'];
    return typeof email === 'string' ? email : '';
  }

  /** The API emits one `roles` claim per role, so the payload may be a string or an array. */
  private hasRole(role: string): boolean {
    const roles = this.getTokenPayload()?.['roles'];

    if (Array.isArray(roles)) {
      return roles.includes(role);
    }

    return roles === role;
  }

  getCurrentUserId(): string {
    const payload = this.getTokenPayload();
    const subject = payload?.['sub'];
    return typeof subject === 'string' ? subject : '';
  }

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    const expires = localStorage.getItem(EXPIRES_KEY);
    if (!expires) return false;
    return new Date(expires) > new Date();
  }

  /** True when the access token will expire within the proactive refresh window. */
  isTokenExpiringSoon(): boolean {
    const expires = localStorage.getItem(EXPIRES_KEY);
    if (!expires) return true;
    return new Date(expires).getTime() - Date.now() < REFRESH_BUFFER_MS;
  }

  private persistSession(res: ILoginResponse): void {
    if (!res.token) return;
    localStorage.setItem(TOKEN_KEY, res.token);
    localStorage.setItem(EXPIRES_KEY, res.expiresAtUtc);
    const orgId = this.extractOrganizationId(res.token);
    if (orgId) localStorage.setItem(ORG_KEY, orgId);
    this.scheduleRefresh(res.expiresAtUtc);
  }

  private scheduleRefresh(expiresAtUtc: string): void {
    this.cancelRefreshTimer();
    const delay = new Date(expiresAtUtc).getTime() - Date.now() - REFRESH_BUFFER_MS;
    if (delay > 0) {
      this._refreshTimerId = setTimeout(() => {
        this.refresh().subscribe({
          error: () => {
            /* clearSession already handled inside refresh */
          },
        });
      }, delay);
    }
  }

  private cancelRefreshTimer(): void {
    if (this._refreshTimerId !== null) {
      clearTimeout(this._refreshTimerId);
      this._refreshTimerId = null;
    }
  }

  private clearSession(): void {
    this.cancelRefreshTimer();
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(EXPIRES_KEY);
    localStorage.removeItem(ORG_KEY);
  }

  private getTokenPayload(): Record<string, unknown> | null {
    const token = this.getToken();
    if (!token) return null;

    return this.parseTokenPayload(token);
  }

  private extractOrganizationId(token: string): string | null {
    const payload = this.parseTokenPayload(token);
    const organizationId =
      payload?.['organizationId'] ?? payload?.['org_id'] ?? payload?.['organization_id'];
    return typeof organizationId === 'string' ? organizationId : null;
  }

  private parseTokenPayload(token: string): Record<string, unknown> | null {
    try {
      const payload = token.split('.')[1];
      if (!payload) return null;

      const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
      return decoded && typeof decoded === 'object' ? (decoded as Record<string, unknown>) : null;
    } catch {
      return null;
    }
  }
}
